import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Sparkles, Film } from 'lucide-react';

const ProductDemo = () => {
  const [step, setStep] = useState(0);
  const [typing, setTyping] = useState(false);
  const [selectedChoice, setSelectedChoice] = useState(null);
  
  // Demo steps: 0=intro, 1=plot choices, 2=dialogue choices, 3=generating, 4=scene output
  const totalSteps = 5;
  
  useEffect(() => {
    const timings = [3000, 4000, 4000, 3000, 5000]; // Time for each step
    
    const timer = setTimeout(() => {
      if (step === 1 || step === 2) {
        // Simulate selection after showing options
        setTimeout(() => setSelectedChoice(0), 1500);
      }
      
      setStep((prev) => (prev + 1) % totalSteps);
      setSelectedChoice(null);
    }, timings[step]);
    
    return () => clearTimeout(timer);
  }, [step]);

  // Typing animation for scene output
  useEffect(() => {
    if (step === 4) {
      setTyping(true);
    } else {
      setTyping(false);
    }
  }, [step]);

  const plotChoices = [
    "Maya discovers a hidden message in her late grandmother's journal",
    "A mysterious stranger approaches Maya with news about her past",
    "Maya finds an old photograph that changes everything"
  ];

  const dialogueChoices = [
    '"This can\'t be real... Grandma, what were you hiding?"',
    '"I need to find out the truth, no matter what it takes."',
    '"Some secrets are meant to stay buried. But not this one."'
  ];

  const generatedScene = `INT. GRANDMOTHER'S ATTIC - NIGHT

Dust motes float in the beam of MAYA's flashlight. She kneels before an old trunk, hands trembling.

MAYA
(whispered)
"This can't be real..."

She holds a weathered journal, pages yellowed with age. A photograph slips out—a young woman who looks exactly like Maya, dated 1952.

MAYA (CONT'D)
"Grandma... what were you hiding?"

The wind HOWLS outside. A floorboard CREAKS behind her. Maya spins around, clutching the journal to her chest.`;

  return (
    <div className="relative w-full aspect-[16/10] bg-[#0a0a0a] border border-white/10 rounded-lg overflow-hidden font-mono text-sm">
      {/* Window chrome */}
      <div className="absolute top-0 left-0 right-0 h-8 bg-[#1a1a1a] border-b border-white/10 flex items-center px-3 gap-2">
        <div className="w-3 h-3 rounded-full bg-red-500/70" />
        <div className="w-3 h-3 rounded-full bg-yellow-500/70" />
        <div className="w-3 h-3 rounded-full bg-green-500/70" />
        <span className="ml-4 text-xs text-muted-foreground">MovieBot — Script Editor</span>
      </div>

      {/* Main content area */}
      <div className="pt-8 h-full">
        <AnimatePresence mode="wait">
          {/* Step 0: Project intro */}
          {step === 0 && (
            <motion.div
              key="intro"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="h-full flex flex-col items-center justify-center p-6"
            >
              <Film className="w-10 h-10 text-primary mb-4" />
              <h3 className="text-white text-lg font-bold mb-2">The Last Letter</h3>
              <p className="text-muted-foreground text-xs mb-4">Drama / Mystery</p>
              <div className="flex items-center gap-2 text-xs">
                <div className="px-2 py-1 bg-primary/20 text-primary rounded">Act 1</div>
                <span className="text-muted-foreground">Beat: Opening Image</span>
              </div>
            </motion.div>
          )}

          {/* Step 1: Plot Direction choices */}
          {step === 1 && (
            <motion.div
              key="plot"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="h-full p-4 overflow-hidden"
            >
              <div className="flex items-center gap-2 mb-3">
                <span className="text-primary text-xs font-bold">STEP 1 OF 3</span>
                <span className="text-white text-xs">Plot Direction</span>
              </div>
              <div className="space-y-2">
                {plotChoices.map((choice, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.15 }}
                    className={`p-3 border rounded text-xs transition-all cursor-pointer ${
                      selectedChoice === idx
                        ? 'border-primary bg-primary/10 text-white'
                        : 'border-white/10 text-muted-foreground hover:border-white/30'
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <span className="text-primary font-bold">
                        {String.fromCharCode(65 + idx)}.
                      </span>
                      <span className="leading-relaxed">{choice}</span>
                      {selectedChoice === idx && (
                        <Check className="w-4 h-4 text-primary ml-auto flex-shrink-0" />
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Step 2: Dialogue choices */}
          {step === 2 && (
            <motion.div
              key="dialogue"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="h-full p-4 overflow-hidden"
            >
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs text-green-400">✓ Plot Direction</span>
              </div>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-primary text-xs font-bold">STEP 2 OF 3</span>
                <span className="text-white text-xs">Dialogue Approach</span>
              </div>
              <div className="space-y-2">
                {dialogueChoices.map((choice, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.15 }}
                    className={`p-3 border rounded text-xs transition-all cursor-pointer ${
                      selectedChoice === idx
                        ? 'border-primary bg-primary/10 text-white'
                        : 'border-white/10 text-muted-foreground hover:border-white/30'
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <span className="text-primary font-bold">
                        {String.fromCharCode(65 + idx)}.
                      </span>
                      <span className="leading-relaxed italic">{choice}</span>
                      {selectedChoice === idx && (
                        <Check className="w-4 h-4 text-primary ml-auto flex-shrink-0" />
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Step 3: Generating */}
          {step === 3 && (
            <motion.div
              key="generating"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="h-full flex flex-col items-center justify-center p-6"
            >
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                className="mb-4"
              >
                <Sparkles className="w-8 h-8 text-primary" />
              </motion.div>
              <p className="text-white text-sm mb-2">Generating your scene...</p>
              <p className="text-muted-foreground text-xs">Applying your creative choices</p>
              
              <div className="mt-4 flex gap-1">
                {[0, 1, 2].map((i) => (
                  <motion.div
                    key={i}
                    className="w-2 h-2 bg-primary rounded-full"
                    animate={{ opacity: [0.3, 1, 0.3] }}
                    transition={{ duration: 1, repeat: Infinity, delay: i * 0.2 }}
                  />
                ))}
              </div>
            </motion.div>
          )}

          {/* Step 4: Scene output */}
          {step === 4 && (
            <motion.div
              key="output"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="h-full p-4 overflow-hidden"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-400" />
                  <span className="text-green-400 text-xs font-bold">Scene Generated</span>
                </div>
                <span className="text-xs text-muted-foreground">Opening Image</span>
              </div>
              
              <div className="bg-black/50 border border-white/10 p-3 rounded h-[calc(100%-2rem)] overflow-hidden">
                <motion.pre 
                  className="text-[10px] leading-relaxed text-white/90 whitespace-pre-wrap font-mono"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  {typing ? (
                    <TypewriterText text={generatedScene} />
                  ) : (
                    generatedScene
                  )}
                </motion.pre>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Progress indicator */}
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
        {[0, 1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className={`w-1.5 h-1.5 rounded-full transition-colors ${
              i === step ? 'bg-primary' : 'bg-white/20'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

// Typewriter effect component
const TypewriterText = ({ text }) => {
  const [displayText, setDisplayText] = useState('');
  
  useEffect(() => {
    let index = 0;
    const interval = setInterval(() => {
      if (index < text.length) {
        setDisplayText(text.slice(0, index + 1));
        index++;
      } else {
        clearInterval(interval);
      }
    }, 15);
    
    return () => clearInterval(interval);
  }, [text]);
  
  return <>{displayText}<span className="animate-pulse">|</span></>;
};

export default ProductDemo;
